import { useCallback, useMemo, useState } from "react";

/**
 * ประวัติคำค้นที่ใช้บ่อย — เก็บใน localStorage ของเครื่องผู้ใช้ (ไม่ผ่าน backend)
 * ใช้กับ chip ใต้ช่องค้นหา: คลิกแล้วเติมคำค้นเดิมได้ทันที
 *
 * กติกา:
 * - นับเป็น "ใช้จริง" เมื่อคำค้นนิ่งครบ RECORD_AFTER_MS (กันคำครึ่ง ๆ ระหว่างพิมพ์)
 * - เรียงด้วยจำนวนครั้งที่ใช้ก่อน แล้วค่อยความสดใหม่ — คำที่ใช้ประจำลอยขึ้น คำหลงมาจมลง
 * - เก็บสูงสุด MAX_ENTRIES รายการ (ตัดตัวที่ใช้น้อย+เก่าสุดทิ้ง)
 */

export type SearchHistoryEntry = { term: string; count: number; lastUsed: number };

export const RECORD_AFTER_MS = 5_000;
const MAX_ENTRIES = 30;

function read(storageKey: string): SearchHistoryEntry[] {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SearchHistoryEntry[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e) => typeof e?.term === "string" && e.term.trim() !== "" && typeof e?.count === "number"
    );
  } catch {
    return []; // localStorage ใช้ไม่ได้ (private mode / quota) — ฟีเจอร์นี้เป็นของเสริม เงียบไว้
  }
}

function write(storageKey: string, entries: SearchHistoryEntry[]): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(entries));
  } catch {
    /* ของเสริม — เขียนไม่ได้ก็ข้าม */
  }
}

function sortByFrequency(entries: SearchHistoryEntry[]): SearchHistoryEntry[] {
  return [...entries].sort((a, b) => b.count - a.count || b.lastUsed - a.lastUsed);
}

export function recordSearchTerm(storageKey: string, rawTerm: string): void {
  const term = rawTerm.trim();
  if (!term) return;
  const entries = read(storageKey);
  const hit = entries.find((e) => e.term === term);
  if (hit) {
    hit.count += 1;
    hit.lastUsed = Date.now();
  } else {
    entries.push({ term, count: 1, lastUsed: Date.now() });
  }
  // ตัดส่วนเกิน: เก็บตัวที่ใช้บ่อย/สดที่สุดไว้
  write(storageKey, sortByFrequency(entries).slice(0, MAX_ENTRIES));
}

export function removeSearchTerm(storageKey: string, term: string): void {
  write(
    storageKey,
    read(storageKey).filter((e) => e.term !== term)
  );
}

export function getTopSearchTerms(storageKey: string, limit: number): SearchHistoryEntry[] {
  return sortByFrequency(read(storageKey)).slice(0, limit);
}

/** ล้างประวัติค้นหาทุกหน้าในเครื่องนี้ — ใช้ตอน logout (เครื่องที่ใช้ร่วมกันจะไม่เห็นคำค้นของคนก่อน)
 *  คีย์ของทุกหน้าลงท้ายด้วย SUFFIX เดียวกัน (เช่น "xian-search-history") */
const STORAGE_KEY_SUFFIX = "-search-history";
export function clearAllSearchHistory(): void {
  try {
    const keys = Object.keys(localStorage).filter((k) => k.endsWith(STORAGE_KEY_SUFFIX));
    for (const k of keys) localStorage.removeItem(k);
  } catch {
    /* ของเสริม — localStorage ใช้ไม่ได้ก็ข้าม */
  }
}

/** hook บาง ๆ — คืนรายการ top + ฟังก์ชัน record/remove ที่กระตุ้น re-render เอง
 *  อ่าน localStorage + JSON.parse เฉพาะตอนที่ค่ามีโอกาสเปลี่ยน (version ขยับ) ไม่ใช่ทุก render */
export function useSearchHistory(storageKey: string, limit: number) {
  const [version, setVersion] = useState(0);
  const record = useCallback(
    (term: string) => {
      recordSearchTerm(storageKey, term);
      setVersion((v) => v + 1);
    },
    [storageKey]
  );
  const remove = useCallback(
    (term: string) => {
      removeSearchTerm(storageKey, term);
      setVersion((v) => v + 1);
    },
    [storageKey]
  );
  const terms = useMemo(() => {
    void version; // อ่าน localStorage ใหม่เฉพาะตอน record/remove ทำให้ version ขยับ
    return getTopSearchTerms(storageKey, limit);
  }, [storageKey, limit, version]);
  return { terms, record, remove };
}
